import { lazy, Suspense, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, BedDouble, Bath, Maximize, Sparkles, Compass } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PROPERTIES, formatPrice } from "@/data/properties";

// Lazy-load the Google Maps mini map so the heavy Maps SDK stays OUT of the
// initial bundle (it's below the fold). A fixed-height placeholder prevents any
// layout shift while the map chunk loads.
const GoogleMiniMap = lazy(() => import("@/components/map/GoogleMiniMap"));

// Client-side chips over the demo dataset (homepage preview).
const FILTERS = [
  { key: "all", label: "All", match: () => true },
  { key: "buy", label: "Buy", match: (p) => p.deal === "For Sale" },
  { key: "rent", label: "Rent", match: (p) => p.deal === "For Rent" },
  { key: "land", label: "Land", match: (p) => p.type === "Land" },
  { key: "commercial", label: "Commercial", match: (p) => p.type === "Commercial" },
];

export const MapSection = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(PROPERTIES[0].id);

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
    const list = PROPERTIES.filter(f.match);
    return list.length ? list : PROPERTIES;
  }, [filter]);

  const activeProp = filtered.find((p) => p.id === active) || filtered[0];

  const points = filtered.filter((p) => p.lat != null && p.lng != null).map((p) => ({
    id: p.id, lat: p.lat, lng: p.lng, price: p.price, deal: p.deal,
  }));

  const onFilter = (key) => {
    setFilter(key);
    const f = FILTERS.find((x) => x.key === key) || FILTERS[0];
    const first = PROPERTIES.filter(f.match)[0] || PROPERTIES[0];
    setActive(first.id);
  };

  return (
    <section className="bg-[#EEF3FC] py-12 dark:bg-[#0E1A33] sm:py-16" data-testid="map-section">
      <div className="abay-container">
        <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Explore Ethiopia on the map</h2>
          <p className="mt-3 text-muted-foreground">Browse verified properties by location — from Addis Ababa to Hawassa and beyond.</p>
        </div>
        <button
          type="button"
          data-testid="map-open-full"
          onClick={() => navigate("/map-search?view=map")}
          className="tap-press inline-flex shrink-0 items-center gap-2 rounded-xl bg-[#1E3A8A] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(30,58,138,0.24)] transition-[filter,box-shadow] duration-200 hover:brightness-[1.05]"
        >
          <Compass className="h-4 w-4" /> Open full map
        </button>
      </div>

      {/* Filter chips */}
      <div className="mt-5 flex flex-wrap gap-2" data-testid="map-filter-chips">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => onFilter(f.key)}
            data-testid={`map-filter-${f.key}`}
            aria-pressed={filter === f.key}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
              filter === f.key
                ? "border-[#1E3A8A] bg-[#1E3A8A] text-white"
                : "border-border bg-card text-muted-foreground hover:border-[#1E3A8A]/30 hover:text-[#1E3A8A] dark:hover:text-[#7AA0FF]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8" data-testid="ethiopia-map">
          <Suspense fallback={<div className="h-[460px] w-full animate-pulse rounded-2xl bg-muted/60 shadow-[var(--shadow-md)]" aria-hidden="true" />}>
            <GoogleMiniMap
              key={`${filter}-${active}`}
              center={{ lat: Number(activeProp.lat), lng: Number(activeProp.lng) }}
              points={points}
              zoom={12}
              height={460}
              className="w-full shadow-[var(--shadow-md)]"
              onPointClick={(pt) => setActive(pt.id)}
            />
          </Suspense>
        </div>

        <div className="lg:col-span-4">
          <div className="flex max-h-[460px] flex-col gap-3 overflow-y-auto pr-1 thin-scroll">
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setActive(p.id)}
                data-testid="map-result-card"
                className={`flex gap-3 rounded-2xl border bg-card p-3 text-left transition-[border-color,box-shadow] ${
                  p.id === active
                    ? "border-[#1E3A8A] shadow-[var(--shadow-md)]"
                    : "border-border hover:border-[#1E3A8A]/30"
                }`}
              >
                <img src={p.image} alt={p.title} loading="lazy" decoding="async" className="h-20 w-24 shrink-0 rounded-xl object-cover" />
                <div className="min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-foreground">{formatPrice(p.price)}</span>
                    <Badge className="gap-1 rounded-full bg-[#10B981]/12 text-[#2f7d34] hover:bg-[#10B981]/12 dark:text-[#34D399]">
                      <Sparkles className="h-3 w-3" /> {p.aiScore}
                    </Badge>
                  </div>
                  <p className="truncate text-sm font-medium text-foreground">{p.title}</p>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {p.location}</p>
                  <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                    {p.beds > 0 && <span className="flex items-center gap-0.5"><BedDouble className="h-3 w-3" /> {p.beds}</span>}
                    {p.baths > 0 && <span className="flex items-center gap-0.5"><Bath className="h-3 w-3" /> {p.baths}</span>}
                    <span className="flex items-center gap-0.5"><Maximize className="h-3 w-3" /> {p.sqm}m²</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};
